import {
  type AuthPayload,
  type AuthRequest,
  type UserWithPermissions,
  type User,
  type OMSAuthResponse,
  P2Role,
  type RecordEntityId,
  MenuRoles,
  FeatureFlags,
} from '@centric-os/types';
import { defineStore, type StateTree } from 'pinia';
import type { RouteLocationNormalized } from 'vue-router';
import { ZendeskWidget, usePermissionMatch, zendesk, getActiveTempScopes } from '../../../helpers';
import type {
  PostUserAuthResponse,
  PostUserOauthResponse,
  GetUserAuthResponse,
  User as CDLUserProfile,
  auth,
  PostUserResetPasswordResponse,
  PostUserResetPasswordTokenResponse,
} from '@compassdigital/sdk.typescript/interface/user';
import type { OptionalPick } from '@centric-os/types/generics';
import { useSiteStore } from '../../sites/use-site-store';
import { useScannerStore } from '../../menus/use-scanner-store';
import { useSplitIoStore } from '../../splitio/use-split-io';
import { useGlobalMenuGroupStore } from '../../menus/use-global-menu-group-store';
import { compact, filter, includes, isEmpty, isNil, map } from 'lodash-es';
import CDLID from '@compassdigital/id';

export const userPermissionScope = {
  fullAdmin: '*:*:*', // cannot be granted via Centric UI
  generic: '*:*',
  genericUser: '*:user',
};

interface State extends StateTree {
  intendedRoute: RouteLocationNormalized;
  user: User;
  cdlUser: UserWithPermissions;
  usedSSO: boolean;
  ssoRedirectError: boolean;
  cdlRealm: string;
  auth: {
    access?: auth['access'];
    refresh?: auth['refresh'];
    oauth?: OMSAuthResponse['oauthaccess'];
  };
  userId: string;
  refreshAuthPromise?: Promise<any>;
  siteOperatorLocalMenuGroupScopes: Array<string>;
  zendesk: ZendeskWidget;
  loggedInOnMobile: boolean;
  isPasswordReset: boolean;
}

export function useAuthStore(storeId = 'auth') {
  const store = defineStore(storeId, {
    state: (): State => ({
      intendedRoute: null,
      user: null,
      cdlUser: null,
      usedSSO: false,
      ssoRedirectError: false,
      cdlRealm: '6MNvqeNgGWSLAv4DoQr7CaKzaNGZl5',
      auth: null,
      userId: '',
      refreshAuthPromise: undefined,
      siteOperatorLocalMenuGroupScopes: [],
      zendesk: zendesk(),
      loggedInOnMobile: false,
      isPasswordReset: false,
    }),
    actions: {
      async handlOMSCodeCallback(authPayload: AuthPayload): Promise<void> {
        await this.authenticate(authPayload);
        if (authPayload.code) {
          this.usedSSO = true;
        }
        await this.getPermissions(this.userId);
      },
      async authenticate({ code, redirectPath = '/home' }: AuthPayload): Promise<void> {
        const {
          access,
          refresh,
          profile,
          oauthaccess: oauth,
          user: userId,
        } = await this.postUserOAuth({ redirectPath, code });
        this.setAuth({ userId, profile, auth: { access, refresh, oauth }, usedSSO: true });
      },
      async p2Authenticate(email: string, password: string) {
        const {
          user: userId,
          token,
          profile,
          access,
          refresh,
        } = await this.getUserAuth({ email, password });
        this.setAuth({ userId, profile, auth: { access, refresh }, usedSSO: false });
      },
      async postUserAuth({ refresh_token }): Promise<PostUserAuthResponse> {
        return this.cdlApi
          .post(`/user/auth?realm=${this.cdlRealm}`, { refresh_token })
          .then((r) => r.data);
      },
      async postUserOAuth({ redirectPath, code }): Promise<PostUserOauthResponse> {
        const isLocalhost = (url: string): boolean => {
          return url.includes('localhost') || url.includes('127.0.0.1');
        };

        const client_id = isLocalhost(window.location.origin)
          ? import.meta.env.VITE_CLIENT_ID_LOCAL
          : import.meta.env.VITE_CLIENT_ID;

        return this.cdlApi
          .post(`/user/oauth`, <AuthRequest>{
            code,
            oms_url: import.meta.env.VITE_SSO_AUTH_URL,
            callback_uri: `${window.location.origin}${
              redirectPath === '/' ? '/order' : redirectPath
            }`,
            client_id: client_id,
          })
          .then((r) => r.data);
      },
      async getUserAuth({ email, password }): Promise<GetUserAuthResponse> {
        return this.cdlApi
          .get(`/user/auth?realm=${this.cdlRealm}`, {
            auth: {
              username: email,
              password,
            },
          })
          .then((r) => r.data);
      },
      async sendUserForgotPassword({
        email,
      }: {
        email: string;
      }): Promise<PostUserResetPasswordResponse> {
        const response = await this.cdlApi.post(`/user/forgotpassword?realm=${this.cdlRealm}`, {
          email,
        });
        return response.data;
      },
      async resetPassword({
        userId,
        resetToken,
        newPassword,
        realm,
        stage,
        role,
      }: {
        userId: string;
        resetToken: string;
        newPassword: string;
        realm: string;
        stage: string;
        role: string;
      }): Promise<PostUserResetPasswordTokenResponse> {
        const response = await this.cdlApi.post(
          `/user/${userId}/resetpassword`,
          {
            password: newPassword,
            realm: realm,
          },
          {
            params: {
              reset_token: resetToken,
              stage: stage,
              role: role,
            },
          },
        );
        return response.data;
      },
      setAuth({
        auth,
        profile,
        userId,
        usedSSO,
      }: {
        auth: State['auth'];
        profile?: CDLUserProfile;
        userId?: string;
        usedSSO?: boolean;
      }) {
        const patchPayload: OptionalPick<State, 'auth' | 'usedSSO' | 'userId' | 'user'> = {
          auth,
        };
        if (profile) {
          patchPayload.user = {
            company: 'Compass Digital',
            firstName: profile.name?.first,
            lastName: profile.name?.last,
            telephone: profile.phone?.toString(),
            email: profile.email,
          };
        }
        if (userId !== undefined) {
          patchPayload.userId = userId;
        }
        if (usedSSO !== undefined) {
          patchPayload.usedSSO = usedSSO;
        }
        this.$patch(patchPayload);
      },
      logout() {
        // Logout from zendesk
        this.zendesk.zendeskLogoutUser();

        // Clear all the auth data
        this.$patch({
          auth: null,
          userId: null,
          user: null,
          cdlUser: null,
          usedSSO: false,
          siteOperatorLocalMenuGroupScopes: [],
        });

        // Clear the site search query and reset the store
        useSiteStore().resetAndClearFilter();
        // Persist is not cleaning everything, so we need to enforce that
        useScannerStore().$reset();
        useSplitIoStore().$reset();
        localStorage.clear();
        sessionStorage.clear();
      },
      async getLocalMenuGroupIdsFromSites(): Promise<RecordEntityId[]> {
        const siteOperatorIds = this.getScopeIds('site_operator_role', 'group');
        if (!siteOperatorIds?.length) {
          return [];
        }

        const siteStore = useSiteStore();

        const promises = siteOperatorIds.map((siteId: string) => {
          return siteStore.getSitesLocalMenuGroups(siteId);
        });
        const sitesLocalMenuGroups = (await Promise.all(promises)).flat();
        const uniqueLmgIds = new Set<RecordEntityId>();
        for (const siteLocalMenuGroup of sitesLocalMenuGroups) {
          if (!isNil(siteLocalMenuGroup?.local_menu_group_id)) {
            uniqueLmgIds.add(siteLocalMenuGroup.local_menu_group_id);
          }
        }

        return Array.from(uniqueLmgIds);
      },
      async getLocalMenuGroupIdsFromGlobalMenuGroups(): Promise<RecordEntityId[]> {
        const globalMenuGroupStore = useGlobalMenuGroupStore();
        await globalMenuGroupStore.getGlobalMenuGroups(true);
        const uniqueLmgIds = new Set<RecordEntityId>();
        for (const globalMenuGroup of globalMenuGroupStore.globalMenuGroups) {
          for (const lmg of globalMenuGroup.allowed_local_menu_groups ?? []) {
            if (!isNil(lmg?.id)) {
              uniqueLmgIds.add(lmg.id);
            }
          }
        }

        return Array.from(uniqueLmgIds);
      },
      async getGlobalMenuGroupManagerPermissions(): Promise<void> {
        const [lmgIdsFromSites, lmgIdsFromGmg] = await Promise.all([
          this.getLocalMenuGroupIdsFromSites(),
          this.getLocalMenuGroupIdsFromGlobalMenuGroups(),
        ]);
        const uniqueLmgIds = new Set([...lmgIdsFromSites, ...lmgIdsFromGmg]);
        const siteOperatorLocalMenuGroupPermissions: string[] = [];
        uniqueLmgIds.forEach((localMenuGroupId) => {
          siteOperatorLocalMenuGroupPermissions.push(`read:local_menu_group:${localMenuGroupId}`);
          siteOperatorLocalMenuGroupPermissions.push(`write:local_menu_group:${localMenuGroupId}`);
          siteOperatorLocalMenuGroupPermissions.push(
            `write_nested:local_menu_group:${localMenuGroupId}`,
          );
        });
        this.$patch({
          siteOperatorLocalMenuGroupScopes: siteOperatorLocalMenuGroupPermissions,
        });
      },
      async getSiteToLocalMenuGroupPermissions(): Promise<void> {
        const splitIoStore = useSplitIoStore();
        const tempScopes = splitIoStore.isFeatureOn(FeatureFlags.TEMP_ADMIN_ACCESS)
          ? getActiveTempScopes(this.cdlUser?.temporary_permissions)
          : [];

        const cdlUserPermissionsScopes = tempScopes.length
          ? tempScopes
          : (this?.cdlUser?.permissions?.scopes ?? []);

        try {
          const hasGlobalMenuGroupManagerRole = cdlUserPermissionsScopes.includes(
            `${MenuRoles.GLOBAL_MENU_GROUP_MANAGER}:menu_v3:role`,
          );
          if (hasGlobalMenuGroupManagerRole) {
            await this.getGlobalMenuGroupManagerPermissions();
            return;
          }
        } catch (error) {
          console.error('Error getting permissions for global menu group manager', error);
          return;
        }

        const siteOperatorIds = this.getScopeIds('site_operator_role', 'group');
        if (!siteOperatorIds?.length) {
          return;
        }

        const siteStore = useSiteStore();

        try {
          const promises = siteOperatorIds.map((siteId: string) => {
            return siteStore.getSitesLocalMenuGroups(siteId);
          });
          const siteToLocalMenuGroupLinks = await Promise.all(promises);
          const hasLimitedEditsRole = cdlUserPermissionsScopes.includes(
            `${MenuRoles.LOCAL_MENU_GROUP_LIMITED_EDITS}:menu_v3:role`,
          );
          if (hasLimitedEditsRole) {
            const siteOperatorLocalMenuGroupPermissions =
              siteToLocalMenuGroupLinks?.flat().reduce((acc, val) => {
                acc.push(`read:local_menu_group:${val?.local_menu_group_id}`);
                acc.push(`write_nested_publish:local_menu_group:${val?.local_menu_group_id}`);
                acc.push(`write_nested_is_active:local_menu_group:${val?.local_menu_group_id}`);
                return acc;
              }, []) || [];
            this.$patch({
              siteOperatorLocalMenuGroupScopes: siteOperatorLocalMenuGroupPermissions,
            });
          } else {
            const siteOperatorLocalMenuGroupPermissions =
              siteToLocalMenuGroupLinks?.flat().reduce((acc, val) => {
                acc.push(`read:local_menu_group:${val?.local_menu_group_id}`);
                acc.push(`write:local_menu_group:${val?.local_menu_group_id}`);
                acc.push(`write_nested:local_menu_group:${val?.local_menu_group_id}`);
                return acc;
              }, []) || [];
            this.$patch({
              siteOperatorLocalMenuGroupScopes: siteOperatorLocalMenuGroupPermissions,
            });
          }
        } catch (error) {
          console.error(error);
        }
      },
      async getPermissions(id: RecordEntityId): Promise<void> {
        const response = await this.cdlApi.get<UserWithPermissions>(`user/${id}/permissions`);
        this.$patch({ cdlUser: response.data });
        await this.getSiteToLocalMenuGroupPermissions();
      },

      async getValidAccessToken(): Promise<string> {
        if (this.refreshAuthPromise) return this.refreshAuthPromise;
        this.refreshAuthPromise = this.getValidAccessTokenCall().finally(() => {
          this.refreshAuthPromise = undefined;
        });
        return this.refreshAuthPromise;
      },

      async getValidAccessTokenCall(): Promise<string> {
        if (!this.auth?.access) return '';
        let { accessValid, oauthValid } = this.getAuthExpiryState();
        if (oauthValid && accessValid) return this.auth.access?.token;
        // handling case when multiple tabs are open and one of them refreshes the token while other has old token
        this.syncAuthWithLocalStorage();
        const {
          accessValid: accessValidLatest,
          refreshValid: refreshValidLatest,
          oauthValid: oauthValidLatest,
        } = this.getAuthExpiryState();
        // if oauth is expired, even though cdl refresh might be valid, we still follow oauth expiry
        if (!oauthValidLatest) return '';
        if (accessValidLatest) return this.auth.access?.token;

        // both access/refresh expired
        if (!refreshValidLatest) return '';
        let refreshResponse: PostUserAuthResponse;
        try {
          refreshResponse = await this.postUserAuth({ refresh_token: this.auth.refresh.token });
        } catch (err) {
          console.error('Retrying auth after error:', err);
          refreshResponse = await this.postUserAuth({
            refresh_token: this.auth.refresh.token,
          }).catch((_err) => {
            console.error('Retry auth failed', _err.message);
            return {};
          });
        }
        if (!refreshResponse.access) {
          this.setAuth({
            auth: null,
          });
          return '';
        }
        this.setAuth({
          auth: {
            refresh: refreshResponse.refresh,
            access: refreshResponse.access,
            oauth: this.auth?.oauth,
          },
        });
        return refreshResponse.access.token;
      },
      syncAuthWithLocalStorage() {
        // handling case when multiple tabs are open and one of them refreshes the token while has old token
        const localStorageAuth = localStorage.getItem('auth');
        if (!localStorageAuth) return; // Exit early if no data in localStorage
        const parsedAuth = JSON.parse(localStorageAuth);
        const accessExpires = new Date(this.auth.access?.expires);
        const refreshExpires = new Date(this.auth.refresh?.expires);
        const oauthExpires = this.auth.oauth?.expires && new Date(this.auth?.oauth?.expires);
        const updateAuthPayload: Partial<State['auth']> = {};
        if (refreshExpires < new Date(parsedAuth?.auth?.refresh?.expires)) {
          updateAuthPayload.refresh = parsedAuth?.auth.refresh;
        }
        if (accessExpires < new Date(parsedAuth?.auth?.access?.expires)) {
          updateAuthPayload.access = parsedAuth?.auth.access;
        }
        if (oauthExpires < new Date(parsedAuth?.auth?.oauth?.expires)) {
          updateAuthPayload.oauth = parsedAuth?.auth.oauth;
        }
        if (!isEmpty(updateAuthPayload)) {
          this.setAuth({ auth: updateAuthPayload });
        }
      },
    },
    getters: {
      hasPermissions: (state: State) => {
        // Helper function to check if a single permission matches against available scopes
        const checkPermissionMatch = (
          neededPermission: string,
          availableScopes: string[],
        ): boolean => {
          const [neededAction, neededService, neededReference] = neededPermission.split(':');

          return availableScopes.some((availablePermission) => {
            const [availableAction, availableService, availableReference] =
              availablePermission.split(':');

            return (
              usePermissionMatch(availableAction, neededAction) &&
              usePermissionMatch(availableService, neededService) &&
              usePermissionMatch(availableReference, neededReference)
            );
          });
        };

        return (neededPermissions: string[] = []): boolean => {
          // USER PERMISSION SCOPES
          const cdlUserPermissionsScopes = state?.cdlUser?.permissions?.scopes || [];
          const filteredCdlUserPermissionsScopes = cdlUserPermissionsScopes.filter(
            (scope) =>
              scope !== userPermissionScope.generic && scope !== userPermissionScope.genericUser,
          );

          // SITE OPERATOR LOCAL MENU GROUP SCOPES
          const siteOperatorLocalMenuGroupScopes = state?.siteOperatorLocalMenuGroupScopes || [];

          // TEMPORARY PERMISSION SCOPES (non-expired, feature-flagged)
          const splitIoStore = useSplitIoStore();
          const tempScopes =  splitIoStore.isFeatureOn(FeatureFlags.TEMP_ADMIN_ACCESS)
            ? getActiveTempScopes(state.cdlUser?.temporary_permissions) 
            : [];

          // ALL FILTERED AND AVAILABLE SCOPES
          const availableScopes = tempScopes.length ? 
            tempScopes :
            [
              ...filteredCdlUserPermissionsScopes,
              ...siteOperatorLocalMenuGroupScopes,
            ];

          // CHECK IF THE USER HAS ALL THE NEEDED PERMISSIONS
          return neededPermissions.every((neededPermission) =>
            checkPermissionMatch(neededPermission, availableScopes),
          );
        };
      },
      hasRole: (state: State) => {
        return (role: string, strict: boolean = false): boolean => {
          const scopes = state.cdlUser?.permissions?.scopes;
          
          if (isEmpty(scopes)) return false;

          const splitIoStore = useSplitIoStore();
          const tempScopes = splitIoStore.isFeatureOn(FeatureFlags.TEMP_ADMIN_ACCESS)
            ? getActiveTempScopes(state.cdlUser?.temporary_permissions)
            : [];

          const effectiveScopes = tempScopes.length ? tempScopes: scopes;

          // TODO:CF-1100 - follow up isFullAdmin condition
          const isFullAdmin = includes(effectiveScopes, userPermissionScope.fullAdmin);

          if (isFullAdmin) return !strict || role === P2Role.ADMIN;

          return effectiveScopes.some((scope) => scope.split(':')[0] === `${role}_role`);
        };
      },
      getRole(state: State): P2Role | null {
        const permissions = state.cdlUser?.permissions;

        if (isEmpty(permissions)) return null;

        const splitIoStore = useSplitIoStore();
        const tempScopes = splitIoStore.isFeatureOn(FeatureFlags.TEMP_ADMIN_ACCESS)
          ? getActiveTempScopes(state.cdlUser?.temporary_permissions)
          : [];
        
        const effectiveScopes = tempScopes.length ? tempScopes : permissions.scopes;

        for (const scope of effectiveScopes) {
          const scopeRole = scope.split(':')[0].replace('_role', '') as P2Role;
          if (includes(Object.values(P2Role), scopeRole)) {
            return scopeRole;
          }
        }
        return null;
      },
      getUserAllowedResourceIds(state: State): string[] {
        const role = this.getRole as P2Role | null;
        if ([P2Role.ADMIN, P2Role.SYS_ADMIN].includes(role)) return ['*'];

        const splitIoStore = useSplitIoStore();
        const tempScopes = splitIoStore.isFeatureOn(FeatureFlags.TEMP_ADMIN_ACCESS)
          ? getActiveTempScopes(state.cdlUser?.temporary_permissions)
          : [];

        const effectiveScopes = tempScopes.length
          ? tempScopes
          : (state.cdlUser?.permissions?.scopes ?? []);

        return (
          effectiveScopes
            .filter((scope) => {
              return (
                scope.includes(P2Role.SITE_OPERATOR) ||
                scope.includes(P2Role.IM_USER) ||
                scope.includes(P2Role.MENU_USER) ||
                scope.includes(P2Role.DC_TEAM) ||
                scope.includes(P2Role.RUNNER)
              );
            })
            .map((scopeId) => {
              return scopeId.split(':')[2];
            }) || []
        );
      },
      getScopeIds: (state: State) => {
        return (action: string, service: string): string[] => {
          const splitIoStore = useSplitIoStore();
          const tempScopes = splitIoStore.isFeatureOn(FeatureFlags.TEMP_ADMIN_ACCESS)
            ? getActiveTempScopes(state.cdlUser?.temporary_permissions)
            : [];

          const scopes = tempScopes.length
            ? tempScopes
            : [
                ...(state.cdlUser?.permissions?.scopes || []),
                ...(state.siteOperatorLocalMenuGroupScopes || []),
              ];

          const filteredScopes = filter(scopes, (scope) => {
            const [scopeAction, scopeService, scopeId] = scope.split(':');
            return (
              (scopeService === service || scopeService === '*') &&
              scopeId !== '*' &&
              (scopeAction === action || scopeAction === '*')
            );
          });

          const scopeIds = map(filteredScopes, (scope) => {
            const parts = scope.split(':');
            return parts.length === 3 ? parts[2] : undefined;
          });

          return compact(scopeIds); // Removes any undefined values
        };
      },
      /**
       * Determines if the user is a site operator for a single site group.
       * This is based on the user's allowed resource IDs and checks if the first resource ID
       * belongs to a group type. If there is exactly one allowed resource and it is a group,
       * the method returns `true`.
       *
       * @param {State} state - The state object containing necessary information for the check.
       * @returns {boolean} `true` if the user has only one allowed resource and it is a group, otherwise `false`.
       */
      isSiteOperatorSingleSite(): boolean {
        const allowedResourceIds = this.getSiteOperatorAllowedResourceIds as string[];
        const firstResourceId = allowedResourceIds[0];

        const shouldRedirectToFirstSite =
          allowedResourceIds.length === 1 && CDLID.contains(firstResourceId, { type: 'group' });

        return shouldRedirectToFirstSite;
      },

      /**
       * Retrieves the list of site operator allowed resource IDs that belong to a group type.
       * Filters the user's allowed resource IDs to include only those that are valid CDL group IDs.
       *
       * @param {State} state - The state object containing necessary information for the operation.
       * @returns {string[]} An array of resource IDs that are validated as belonging to a CDL group.
       */
      getSiteOperatorAllowedResourceIds(): string[] {
        const allowedResourceIds = this.getUserAllowedResourceIds as string[];

        // Validate CDL IDs
        const validCDLIDs = allowedResourceIds.filter((id) =>
          CDLID.contains(id, { type: 'group' }),
        );

        return validCDLIDs;
      },
      getAuthExpiryState: (state: State) => {
        return (): {
          accessValid?: boolean;
          refreshValid?: boolean;
          oauthValid?: boolean;
        } => {
          try {
            const now = new Date(Date.now() + 30 * 1000);
            const accessValid = new Date(state.auth.access?.expires);
            const refreshValid = new Date(state.auth.refresh?.expires);
            const oauthValid =
              state.auth.oauth?.expires && new Date(parseInt(state.auth?.oauth?.expires));
            return {
              accessValid: state.auth.access?.expires && accessValid > now,
              refreshValid: state.auth.refresh?.expires && refreshValid > now,
              oauthValid: !state.auth.oauth?.expires || oauthValid > now,
            };
          } catch (err) {
            console.error(err);
            return {
              accessValid: false,
              refreshValid: false,
              oauthValid: false,
            };
          }
        };
      },
      isAuthUser: (state: State) => {
        return (ap3UserId: string): boolean => {
          return state.cdlUser?.id === ap3UserId;
        };
      },
    },
    persist: {
      storage: localStorage,
      paths: ['auth', 'user', 'userId', 'cdlUser', 'usedSSO', 'siteOperatorLocalMenuGroupScopes'],
    },
    pagination: false,
  });
  return store();
}
